/* ============================================================================
 * Hooks das telas de DOCUMENTOS FISCAIS (NFC-e / NFS-e / Pipeline / De-para /
 * Eventos & CC-e).
 *
 * Segue o padrão de `useFiscalInvoices` (features/fiscal): useQuery +
 * useMutation com `apiClient` (shared/api/client), invalidação no success.
 *
 * As query keys são locais a esta feature (`fiscalDocuments(...)`), preservando
 * o contrato "criar somente arquivos em features/fiscal" — nada aqui toca
 * shared/api/query-keys.ts.
 *
 * Rotas reais consumidas (prefixo /api/v1):
 *   GET  /fiscal/invoices?type=NFCE|NFSE&status=&page=&limit=
 *   GET  /fiscal/invoices/:id
 *   GET  /fiscal/nfse-pipeline?status=&page=&limit=
 *   GET  /fiscal/product-mapping?xmlKey=&confidence=&page=&limit=
 *   POST /fiscal/product-mapping
 *   PUT  /fiscal/product-mapping/:id
 *   DELETE /fiscal/product-mapping/:id
 *   GET  /fiscal/events?days=&limit=
 *   POST /fiscal/events/:id/cc-e
 *   GET  /products?limit=200   (catálogo, para resolver nome do produto no de-para)
 * ========================================================================== */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';
import type {
  PaginatedFiscal,
  FiscalDocumentListItem,
  FiscalDocumentDetail,
  FiscalDocumentsQuery,
  NfsePipelineItem,
  NfsePipelineQuery,
  ProductMapping,
  CreateProductMappingInput,
  UpdateProductMappingInput,
  ProductMappingQuery,
  CatalogProduct,
  FiscalEventsSnapshot,
  CreateCceInput,
  GeneratedCce,
} from './fiscalDocumentsTypes';

/* ── Query keys locais ── */

const fiscalDocumentsKeys = {
  invoices: (params: Record<string, unknown>) => ['fiscal', 'documents', 'invoices', params] as const,
  invoiceDetail: (id: string) => ['fiscal', 'documents', 'invoices', id] as const,
  pipeline: (params: Record<string, unknown>) => ['fiscal', 'documents', 'nfse-pipeline', params] as const,
  mappings: (params: Record<string, unknown>) => ['fiscal', 'documents', 'product-mapping', params] as const,
  events: (params: Record<string, unknown>) => ['fiscal', 'documents', 'events', params] as const,
  catalog: () => ['fiscal', 'documents', 'catalog'] as const,
};

/* ── 1. Listagens NFC-e / NFS-e ── */

export function useFiscalDocuments(
  tenantId: string,
  params: FiscalDocumentsQuery = {},
) {
  const { type, status, documentNumber, accessKey, page, limit } = params;
  return useQuery({
    queryKey: fiscalDocumentsKeys.invoices({ tenantId, type, status, documentNumber, accessKey, page, limit }),
    queryFn: () =>
      apiClient.get<PaginatedFiscal<FiscalDocumentListItem>>('/fiscal/invoices', {
        params: {
          ...(type ? { type } : {}),
          ...(status && status !== 'ALL' ? { status } : {}),
          ...(documentNumber ? { documentNumber } : {}),
          ...(accessKey ? { accessKey } : {}),
          ...(page ? { page: String(page) } : {}),
          ...(limit ? { limit: String(limit) } : {}),
        },
      }),
    enabled: !!tenantId,
  });
}

/** Detalhe de um documento (GET /fiscal/invoices/:id). */
export function useFiscalDocumentDetail(tenantId: string, id: string | null) {
  return useQuery({
    queryKey: fiscalDocumentsKeys.invoiceDetail(id ?? ''),
    queryFn: () => apiClient.get<FiscalDocumentDetail>(`/fiscal/invoices/${id}`),
    enabled: !!tenantId && !!id,
  });
}

/* ── 2. Pipeline de Faturamento NFS-e ── */

export function useNfsePipeline(tenantId: string, params: NfsePipelineQuery = {}) {
  const { status, page, limit } = params;
  return useQuery({
    queryKey: fiscalDocumentsKeys.pipeline({ tenantId, status, page, limit }),
    queryFn: () =>
      apiClient.get<PaginatedFiscal<NfsePipelineItem>>('/fiscal/nfse-pipeline', {
        params: {
          ...(status && status !== 'ALL' ? { status } : {}),
          ...(page ? { page: String(page) } : {}),
          ...(limit ? { limit: String(limit) } : {}),
        },
      }),
    enabled: !!tenantId,
  });
}

/* ── 3. De-para de Produtos XML × Estoque (CRUD) ── */

export function useProductMappings(tenantId: string, params: ProductMappingQuery = {}) {
  const { xmlKey, confidence, page, limit } = params;
  return useQuery({
    queryKey: fiscalDocumentsKeys.mappings({ tenantId, xmlKey, confidence, page, limit }),
    queryFn: () =>
      apiClient.get<PaginatedFiscal<ProductMapping>>('/fiscal/product-mapping', {
        params: {
          ...(xmlKey ? { xmlKey } : {}),
          ...(confidence && confidence !== 'ALL' ? { confidence } : {}),
          ...(page ? { page: String(page) } : {}),
          ...(limit ? { limit: String(limit) } : {}),
        },
      }),
    enabled: !!tenantId,
  });
}

/** Catálogo de produtos (GET /products — array direto) p/ resolver nomes no de-para. */
export function useCatalogProducts(tenantId: string) {
  return useQuery({
    queryKey: fiscalDocumentsKeys.catalog(),
    queryFn: () =>
      apiClient.get<CatalogProduct[]>('/products', {
        params: { tenantId, limit: '200' },
      }),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateProductMapping(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductMappingInput) =>
      apiClient.post<ProductMapping>('/fiscal/product-mapping', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: fiscalDocumentsKeys.mappings({ tenantId }) }),
  });
}

export function useUpdateProductMapping(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProductMappingInput }) =>
      apiClient.put<ProductMapping>(`/fiscal/product-mapping/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: fiscalDocumentsKeys.mappings({ tenantId }) }),
  });
}

export function useDeleteProductMapping(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/fiscal/product-mapping/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: fiscalDocumentsKeys.mappings({ tenantId }) }),
  });
}

/* ── 4. Eventos Fiscais & Carta de Correção ── */

export function useFiscalEvents(tenantId: string, days = 30, limit = 20) {
  return useQuery({
    queryKey: fiscalDocumentsKeys.events({ tenantId, days, limit }),
    queryFn: () =>
      apiClient.get<FiscalEventsSnapshot>('/fiscal/events', {
        params: { days: String(days), limit: String(limit) },
      }),
    enabled: !!tenantId,
  });
}

export function useGenerateCce(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateCceInput }) =>
      apiClient.post<GeneratedCce>(`/fiscal/events/${id}/cc-e`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fiscalDocumentsKeys.events({ tenantId }) });
      qc.invalidateQueries({ queryKey: fiscalDocumentsKeys.invoices({ tenantId }) });
    },
  });
}